import { uploadFileResumable } from 'modules/files/utils/resumableGcsUpload'
import { BackendService } from 'services/BackendService'

import type {
  CreateConversationFollowUpUploadResponse,
  CreateConversationRequestBody,
  CreateConversationResponse201,
  components,
} from '@startech/openapi'

type UpdateFileBody = components['schemas']['UpdateFileBody']

export const createConversationUpload = async (input: {
  duration?: number
  filename: string
  mimeType: string
  sizeBytes: number
}): Promise<CreateConversationResponse201> => {
  const payload: CreateConversationRequestBody = {
    duration: input.duration ?? undefined,
    filename: input.filename,
    mimeType: input.mimeType,
    sizeBytes: input.sizeBytes,
  }

  return BackendService.POST<CreateConversationResponse201>('conversations', payload)
}

export const createConversationFollowUpUpload = async (
  conversationId: string,
  input: {
    duration?: number
    filename: string
    mimeType: string
    sizeBytes: number
  }
): Promise<CreateConversationFollowUpUploadResponse> => {
  const payload: CreateConversationRequestBody = {
    duration: input.duration ?? undefined,
    filename: input.filename,
    mimeType: input.mimeType,
    sizeBytes: input.sizeBytes,
  }

  return BackendService.POST<CreateConversationFollowUpUploadResponse>(
    `conversations/${conversationId}/uploads`,
    payload
  )
}

/**
 * `uploadUrl` is a v4 signed URL for action `resumable` (initiate session via POST).
 */
export const uploadFileToSignedUrl = async (
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal
): Promise<void> => {
  await uploadFileResumable({
    initSignedUrl: uploadUrl,
    file,
    onProgress,
    signal,
  })
}

const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1'])

const shouldSimulateUploadFinalizeLocally = (): boolean => {
  if (typeof globalThis === 'undefined') {
    return false
  }

  return process.env.NODE_ENV !== 'production' && LOCALHOST_HOSTNAMES.has(globalThis.location.hostname)
}

export const finalizeConversationUploadForLocalDevelopment = async (
  conversationId: string,
  fileId?: string,
  signal?: AbortSignal
): Promise<void> => {
  if (!shouldSimulateUploadFinalizeLocally()) {
    return
  }

  await BackendService.POST<{ conversationId: string; fileId?: string }>(
    `conversations/${conversationId}/dev-finalize-upload`,
    fileId ? { fileId } : {},
    {
      signal,
    }
  )
}

export const markFileUploadFailed = async (
  fileId: string,
  errorMessage: string,
  signal?: AbortSignal
): Promise<void> => {
  const payload: UpdateFileBody = {
    processingError: errorMessage,
    processingStatus: 'failed',
  }

  await BackendService.PATCH(`files/${fileId}`, payload, {
    signal,
  })
}
